import CheckIcon from "@mui/icons-material/Check";
import {
  Typography,
  InputAdornment,
  TextField,
  CircularProgress,
  IconButton,
  styled,
  Box
} from "@mui/material";
import { Stack } from "@mui/system";
import { skipToken } from "@reduxjs/toolkit/query";
import { ChangeEvent, useState } from "react";
import { useTranslation } from "react-i18next";

import { useAppSelector } from "~/app/store";

import { percentRegex } from "~/lib/helpers";
import { selectUsersClientId } from "~/redux/selectors/authSelectors";
import { selectUsersFulfillmentCenter } from "~/redux/selectors/storeSelectors";
import {
  useGetBinConfigurationsQuery,
  usePatchBinConfigurationMutation
} from "~/redux/warehouse/bin.hooks";
import { AutostoreBinConfigurationDto } from "~/types/api";

type AutostoreBinConfigurationDtoExtended = AutostoreBinConfigurationDto & {
  newMaxBinFillPercent?: string;
  isDirty?: boolean;
  hasError?: boolean;
};

const numberOfCompartmentsToBinTypeMap = {
  1: "whole bins",
  2: "half bins",
  4: "quarter bins"
} as const;
type numberOfCompartmentsType = keyof typeof numberOfCompartmentsToBinTypeMap;

const CheckmarkButton = styled(IconButton)(({ theme }) => ({
  color: theme.palette.primary.dark,
  backgroundColor: "#0000000a",
  width: "55px",
  height: "55px",
  borderRadius: "10px"
}));

function BinFillCapacity() {
  const { t } = useTranslation();
  const usersFulfillmentCenter = useAppSelector(selectUsersFulfillmentCenter);
  const usersClientId = useAppSelector(selectUsersClientId);
  const [modifiedBinConfigurationsById, setModifiedBinConfigurationsById] =
    useState<Record<Guid, AutostoreBinConfigurationDtoExtended>>({});

  const { isLoading, data: binConfigurations } = useGetBinConfigurationsQuery(
    usersClientId && usersFulfillmentCenter ? undefined : skipToken
  );

  const [patch, { isLoading: isPatching }] = usePatchBinConfigurationMutation();

  const onPercentChange = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    {
      configurationId,
      maxBinFillPercent
    }: Pick<
      AutostoreBinConfigurationDto,
      "configurationId" | "maxBinFillPercent"
    >
  ) => {
    const { value } = event.target;
    setModifiedBinConfigurationsById((prevState) => ({
      ...prevState,
      [configurationId]: {
        ...prevState[configurationId],
        isDirty: true,
        maxBinFillPercent,
        newMaxBinFillPercent: value
      }
    }));
  };

  const onUpdateMaxFillPercent = async (configurationId: string) => {
    const { maxBinFillPercent, newMaxBinFillPercent } =
      modifiedBinConfigurationsById[configurationId];
    const isValidNumber =
      newMaxBinFillPercent && percentRegex.test(newMaxBinFillPercent);
    if (isValidNumber && +newMaxBinFillPercent !== maxBinFillPercent) {
      await patch({
        configurationId,
        maxBinFillPercent: +newMaxBinFillPercent
      }).unwrap();
      setModifiedBinConfigurationsById((prevState) => ({
        ...prevState,
        [configurationId]: {
          ...prevState[configurationId],
          isDirty: false,
          hasError: false
        }
      }));
    } else {
      setModifiedBinConfigurationsById((prevState) => ({
        ...prevState,
        [configurationId]: {
          ...prevState[configurationId],
          hasError: true
        }
      }));
    }
  };

  return isLoading ? (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        height: "100%",
        justifyContent: "center"
      }}
    >
      <CircularProgress size={30} />
    </Box>
  ) : (
    <Stack
      sx={{
        p: 6.25,
        gap: 3,
        boxSizing: "border-box",
        flexDirection: "column",
        width: "100%"
      }}
    >
      <Typography variant="h5">{t("bin fill capacity")}</Typography>

      <Stack
        alignItems="center"
        flexDirection="row"
        sx={{
          flexWrap: "wrap",
          rowGap: 3,
          columnGap: 1,
          justifyContent: {
            xs: "center",
            md: "flex-end"
          }
        }}
      >
        {binConfigurations?.map(
          ({ configurationId, numberOfCompartments, maxBinFillPercent }) => (
            <Stack
              key={configurationId}
              width="unset"
              gap={0.5}
              flexDirection="row"
              alignItems="flex-start"
            >
              <TextField
                disabled={isPatching}
                label={t(
                  numberOfCompartmentsToBinTypeMap[
                    numberOfCompartments as numberOfCompartmentsType
                  ]
                )}
                onChange={(event) =>
                  onPercentChange(event, {
                    configurationId,
                    maxBinFillPercent
                  })
                }
                value={
                  modifiedBinConfigurationsById[configurationId]?.isDirty
                    ? modifiedBinConfigurationsById[configurationId]
                        .newMaxBinFillPercent
                    : maxBinFillPercent
                }
                sx={{ width: "160px" }}
                error={modifiedBinConfigurationsById[configurationId]?.hasError}
                helperText={
                  modifiedBinConfigurationsById[configurationId]?.hasError
                    ? "Must be number 1-100"
                    : null
                }
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">%</InputAdornment>
                  )
                }}
              />
              <Box
                sx={{
                  minWidth: "55px",
                  mt: 0.5
                }}
              >
                {modifiedBinConfigurationsById[configurationId]?.isDirty && (
                  <CheckmarkButton
                    aria-label="Update max fill percent"
                    disabled={isPatching}
                    onClick={() => onUpdateMaxFillPercent(configurationId)}
                  >
                    <CheckIcon />
                  </CheckmarkButton>
                )}
              </Box>
            </Stack>
          )
        )}
      </Stack>
    </Stack>
  );
}

export default BinFillCapacity;
